import React from "react";
import cn from "classnames";
import styles from "./Preview.module.sass";

const PLAY_NOW = {
  es: "Jugar ahora",
  en: "Play now",
  fr: "Jouer maintenant",
  pt: "Jogar agora",
  ru: "Играть",
};

const nowTime = () =>
  new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });

/** Push: en el móvil real el texto se muestra plano (los <br/> etc. no se renderizan). */
const stripHtml = (s = "") => {
  if (!s) return "";
  const div = document.createElement("div");
  div.innerHTML = String(s).replace(/<br\s*\/?>/gi, "\n");
  return (div.textContent || "").trim();
};

/** ¿El cuerpo contiene HTML (para decidir si se pinta como HTML o como texto)? */
const looksLikeHtml = (s = "") => /<[a-z][^>]*>/i.test(String(s || ""));

/** Icono real de la app (logo de BoostChinese servido por el propio backoffice). */
const AppIcon = ({ size = 38 }) => (
  <img
    className={styles.appIcon}
    src="/images/boost-app-icon.png"
    alt=""
    style={{ width: size, height: size }}
  />
);

/** Push v2: lock screen con wallpaper, hora grande y notificación "frosted glass". */
const PushPreview = ({ title, body, imageUrl }) => {
  const plainTitle = stripHtml(title);
  const plainBody = stripHtml(body);
  return (
    <div className={styles.phone}>
      <div className={cn(styles.screen, styles.lockScreen)}>
        <div className={styles.island} />
        <div className={styles.lockClock}>
          <div className={styles.lockDate}>
            {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" })}
          </div>
          <div className={styles.lockTime}>{nowTime()}</div>
        </div>
        <div className={styles.notifStack}>
          <div className={styles.notification}>
            <AppIcon size={36} />
            <div className={styles.notifContent}>
              <div className={styles.notifHead}>
                <span className={styles.notifApp}>Boost Chinese</span>
                <span className={styles.notifTime}>ahora</span>
              </div>
              <div className={styles.notifTitle}>{plainTitle || "Título de la notificación"}</div>
              <div className={styles.notifBody}>{plainBody || "El cuerpo del push aparecerá aquí."}</div>
            </div>
            {imageUrl && (
              <img
                className={styles.notifImage}
                src={imageUrl}
                alt=""
                onError={(e) => (e.target.style.display = "none")}
              />
            )}
          </div>
        </div>
        <div className={styles.lockBottom}>
          <span className={styles.lockCircle}>🔦</span>
          <span className={styles.lockCircle}>📷</span>
        </div>
        <div className={styles.homeIndicator} />
      </div>
    </div>
  );
};

/** In-app v2: réplica del MODAL translúcido real de la app (hero + kicker + CTA). */
const InAppPreview = ({ title, body, imageUrl, linkUrl, linkTitle, webViewUrl }) => (
  <div className={styles.phone}>
    <div className={cn(styles.screen, styles.inappScreen)}>
      <div className={styles.island} />
      <div className={styles.inappModal}>
        {imageUrl ? (
          <div className={styles.inappHeroWrap}>
            <img
              className={styles.inappHero}
              src={imageUrl}
              alt=""
              onError={(e) => (e.target.style.display = "none")}
            />
            <div className={styles.inappHeroFade} />
            <span className={styles.inappClose}>✕</span>
          </div>
        ) : (
          <span className={cn(styles.inappClose, styles.inappCloseFloat)}>✕</span>
        )}
        <div className={styles.inappBody}>
          <div className={styles.inappKicker}>Novedades de Bāo</div>
          <div className={styles.inappTitle}>{stripHtml(title) || "Título del mensaje"}</div>
          {body ? (
            looksLikeHtml(body) ? (
              <div className={styles.inappText} dangerouslySetInnerHTML={{ __html: body }} />
            ) : (
              <div className={styles.inappText}>{body}</div>
            )
          ) : (
            <div className={styles.inappText}>El cuerpo del mensaje in-app aparecerá aquí.</div>
          )}
        </div>
        <div className={styles.inappFooter}>
          {(linkUrl || webViewUrl) && (
            <div className={styles.inappCta}>{linkTitle || "Ver más"}</div>
          )}
          <div className={styles.inappSecondary}>Ir a la app</div>
        </div>
      </div>
    </div>
  </div>
);

/** Réplica aproximada del NewsTemplate corporativo dentro de un cliente de correo. */
const MailPreview = ({ lang, title, bodyHtml, imageUrl, linkUrl, linkTitle, sections }) => (
  <div className={styles.mailClient}>
    <div className={styles.mailChrome}>
      <span /><span /><span />
    </div>
    <div className={styles.mailMeta}>
      <div>
        <span className={styles.mailMetaLabel}>De:</span> BoostChinese &lt;hello@boostchinese.com&gt;
      </div>
      <div>
        <span className={styles.mailMetaLabel}>Asunto:</span>{" "}
        <strong>{title || "Asunto del email"}</strong>
      </div>
    </div>
    <div className={styles.mailCanvas}>
      <div className={styles.mailInner}>
        <div className={styles.mailTop}>
          <span className={styles.mailWebversion}>View this email in your browser</span>
          <span className={styles.mailLogo}>
            BOOST<span>CHINESE</span>
          </span>
        </div>
        <div className={styles.mailTitle}>
          <span className={styles.mailEmoji}>🎉</span>
          {title || "Asunto del email"}
        </div>
        {imageUrl && (
          <img className={styles.mailImage} src={imageUrl} alt="" onError={(e) => (e.target.style.display = "none")} />
        )}
        <div
          className={styles.mailBody}
          dangerouslySetInnerHTML={{
            __html: bodyHtml || "<p style='color:#889'>El contenido del email aparecerá aquí…</p>",
          }}
        />
        {/* Secciones numeradas de la newsletter de Bāo (una por novedad). */}
        {sections?.map((section, index) => (
          <div className={styles.mailSection} key={index}>
            <div className={styles.mailSectionHead}>
              <span className={styles.mailSectionNum}>{index + 1}</span>
              <span className={styles.mailSectionTitle}>
                {section.title?.[lang] || section.title?.en || "Titular de la sección"}
              </span>
            </div>
            <div className={styles.mailSectionBody}>
              {section.body?.[lang] || section.body?.en || "Texto de la sección…"}
            </div>
            {(section.imageByLang?.[lang] || section.imageUrl) && (
              <img
                className={styles.mailSectionImage}
                src={section.imageByLang?.[lang] || section.imageUrl}
                alt=""
                onError={(e) => (e.target.style.display = "none")}
              />
            )}
          </div>
        ))}
        <div className={styles.mailCta}>{linkTitle || PLAY_NOW[lang] || PLAY_NOW.en}</div>
        {linkUrl && <div className={styles.mailCtaUrl}>→ {linkUrl}</div>}
        <div className={styles.mailFooter}>
          BoostChinese · Learn Chinese playing
          <br />
          <span>Unsubscribe</span> · <span>Preferences</span>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Preview en vivo del mensaje, con el frame adecuado al canal.
 * `title`/`body` llegan ya resueltos al idioma activo (con fallback).
 */
/**
 * Correo tal cual lo genera el servidor (misma plantilla, idioma y capturas que el
 * envío real) dentro del marco de cliente de correo. El iframe se ajusta al alto
 * del contenido para ver el correo entero haciendo scroll en la columna.
 */
const MAIL_WIDTH = 640;

const ServerMailPreview = ({ subject, html, loading }) => {
  const wrapRef = React.useRef(null);
  const [height, setHeight] = React.useState(1200);
  const [scale, setScale] = React.useState(0.5);

  // El correo mide ~600 px de ancho: se pinta a su tamaño real y se escala para
  // que quepa entero en la columna, sin cortes ni scroll horizontal.
  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const update = () => setScale(Math.min(1, el.clientWidth / MAIL_WIDTH));
    update();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);

  const onLoad = (e) => {
    try {
      const doc = e.target.contentDocument;
      const measure = () => setHeight(doc.documentElement.scrollHeight || doc.body.scrollHeight);
      measure();
      // Las imágenes llegan después: se reajusta cuando terminan de cargar.
      doc.querySelectorAll("img").forEach((img) => img.addEventListener("load", measure));
    } catch (err) {}
  };

  return (
    <div className={styles.mailClient}>
      <div className={styles.mailChrome}>
        <span /><span /><span />
      </div>
      <div className={styles.mailMeta}>
        <div>
          <span className={styles.mailMetaLabel}>De:</span> BoostChinese &lt;hello@boostchinese.com&gt;
        </div>
        <div>
          <span className={styles.mailMetaLabel}>Asunto:</span>{" "}
          <strong>{(subject || "Asunto del email").replace(/<[^>]*>/g, "")}</strong>
        </div>
      </div>
      <div ref={wrapRef} className={styles.serverMail} style={{ height: html ? height * scale : undefined }}>
        {loading && !html && <div className={styles.serverMailLoading}>Generando el correo…</div>}
        {html && (
          <iframe
            title="Vista previa del correo"
            className={styles.serverMailFrame}
            style={{ width: MAIL_WIDTH, height, transform: `scale(${scale})`, transformOrigin: "0 0" }}
            srcDoc={html}
            sandbox="allow-same-origin"
            scrolling="no"
            onLoad={onLoad}
          />
        )}
      </div>
    </div>
  );
};

const Preview = ({
  serverMail,
  channel,
  lang,
  title,
  body,
  bodyHtml,
  imageUrl,
  linkUrl,
  linkTitle,
  webViewUrl,
  sections,
}) => {
  if (channel === "mail" && serverMail && (serverMail.html || serverMail.loading)) {
    return <ServerMailPreview {...serverMail} />;
  }
  if (channel === "mail") {
    return (
      <MailPreview
        lang={lang}
        title={title}
        bodyHtml={bodyHtml}
        imageUrl={imageUrl}
        linkUrl={linkUrl}
        linkTitle={linkTitle}
        sections={sections}
      />
    );
  }
  if (channel === "inapp") {
    return (
      <InAppPreview
        title={title}
        body={body}
        imageUrl={imageUrl}
        linkUrl={linkUrl}
        linkTitle={linkTitle}
        webViewUrl={webViewUrl}
      />
    );
  }
  return <PushPreview title={title} body={body} imageUrl={imageUrl} />;
};

export default Preview;
